import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Collage } from "./Collage";
import { useLightbox } from "./Lightbox";
import { Reveal } from "./Motion";
import { caseStudies, otherWork } from "../data/projects";
import { scrollToTarget } from "../lib/smooth";

const Featured = ({ p }) => (
    <article className="group" data-testid={`case-study-card-${p.slug}`}>
      <Reveal>
        <div className="mb-5 grid grid-cols-12 items-end gap-4 border-t border-ink pt-4">
          <span className="col-span-2 font-mono text-xs text-muted md:col-span-1">{p.index}</span>
          <h3 className="col-span-10 font-display text-3xl font-semibold leading-none tracking-[-0.04em] text-ink md:col-span-6 md:text-5xl">
            <Link to={`/work/${p.slug}`} className="title-link" data-testid={`case-study-title-${p.slug}`}>{p.title}</Link>
          </h3>
          <p className="col-span-8 col-start-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted md:col-span-3 md:col-start-auto">{p.kicker}</p>
          <p className="col-span-2 text-right font-mono text-[11px] tracking-[0.18em] text-muted">{p.year}</p>
        </div>
      </Reveal>
      <Collage rows={p.flow} />
      <Link to={`/work/${p.slug}`} className="pill-btn mt-6" data-testid={`case-study-view-btn-${p.slug}`}>
        <span>Read case study</span><span aria-hidden>→</span>
      </Link>
    </article>
);

const Minor = ({ p, i }) => {
  const { open } = useLightbox();
  const openBook = p.brochure ? () => open(p.brochure, 0, "brochure") : undefined;
  return (
  <article data-testid={`work-item-${i}`}>
    <Reveal>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-line pt-3">
        <h4 className="font-display text-lg font-medium tracking-[-0.02em] text-ink md:text-xl">{p.title}</h4>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">{[p.discipline, p.year].filter(Boolean).join(" · ")}</p>
      </div>
      {p.description && <p className="mb-5 max-w-2xl text-base leading-relaxed text-muted" data-testid={`work-item-description-${i}`}>{p.description}</p>}
    </Reveal>
    <Collage rows={p.flow} onOpen={openBook} />
    {openBook && (
      <button onClick={openBook} className="pill-btn mt-6" data-testid="open-brochure-button">
        <span>Open brochure</span><span aria-hidden>↗</span>
      </button>
    )}
  </article>
  );
};

const order = [
  ["c", 0], ["o", 0], ["c", 1], ["o", 1], ["o", 2], ["c", 2], ["o", 3], ["o", 4], ["o", 5],
];

const FILTERS = ["All", "Branding", "UX", "Print", "CGI", "Events"];
const TAGS = {
  trepanel: ["UX", "Branding", "Print", "CGI"],
  "solihull-tiling-specialists": ["Branding", "UX"],
  "rehearsal-dinner-club": ["Events", "Branding", "Print"],
  "Garden CGI": ["CGI"],
  "Curated Chaos": ["Branding", "UX", "Events", "Print"],
  "Spec Brochures": ["Print"],
  "Large format": ["Print"],
  "Walls and Floors fleet": ["Print"],
  "Retail campaigns": ["Branding"],
};
const tagsOf = ([t, i]) => TAGS[t === "c" ? caseStudies[i].slug : otherWork[i].title] || [];

const FilterBar = ({ active, setActive }) => (
  <div className="sticky top-0 z-40 -mx-5 mb-14 overflow-x-auto bg-[rgba(239,236,230,0.97)] px-5 pb-3 pt-[64px] backdrop-blur-md md:-mx-10 md:mb-20 md:px-10 md:pt-[76px]" data-testid="work-filters">
    <div className="flex w-max gap-2">
      {FILTERS.map((f) => {
        const n = f === "All" ? order.length : order.filter((o) => tagsOf(o).includes(f)).length;
        const on = active === f;
        return (
          <motion.button
            key={f}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setActive(f);
              const top = document.getElementById("work")?.getBoundingClientRect().top ?? 0;
              if (top < 0) scrollToTarget("#work", { duration: 0.9 });
            }}
            className={`relative rounded-full border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${on ? "border-ink text-paper" : "border-line text-ink hover:border-ink"}`}
            data-testid={`work-filter-${f.toLowerCase()}`}
          >
            {on && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-ink" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
            <span className="relative">{f} <sup className={on ? "text-stone-400" : "text-muted"}>{n}</sup></span>
          </motion.button>
        );
      })}
    </div>
  </div>
);

export const ProjectFlow = () => {
  const [active, setActive] = useState("All");
  const shown = order.filter((o) => active === "All" || tagsOf(o).includes(active));
  return (
  <section id="work" className="px-5 pb-24 md:px-10 md:pb-40" data-testid="work-section">
    <div className="-mb-10 flex items-end justify-between md:-mb-12">
      <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink">Selected work <span className="text-muted">/ 2009—Present</span></h2>
      <p className="hidden max-w-xs text-right text-sm text-muted md:block">Three full case studies, plus a flow of print, packaging, brand and CGI work.</p>
    </div>
    <FilterBar active={active} setActive={setActive} />
    <div className="space-y-24 md:space-y-40" data-testid="work-flow-list">
      <AnimatePresence initial={false}>
        {shown.map(([t, i]) => (
          <motion.div
            key={`${t}${i}`}
            layout="position"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {t === "c" ? <Featured p={caseStudies[i]} /> : <Minor p={otherWork[i]} i={i} />}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  </section>
  );
};
